import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Mail, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';

const EMAIL_DEFAULT = 'rmartinez@chirinosteel.com';

function Tabla({ titulo, headers, rows, vacio, testId }) {
  return (
    <div data-testid={testId}>
      <h4 className="text-sm font-semibold mt-4 mb-1">{titulo} <span className="text-slate-400 font-normal">({rows.length})</span></h4>
      {rows.length === 0 ? (
        <p className="text-xs text-slate-500">{vacio}</p>
      ) : (
        <div className="max-h-56 overflow-auto border rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>{headers.map((h) => <th key={h} className="text-left px-2 py-1 font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t">{r.map((c, j) => <td key={j} className="px-2 py-1">{c ?? ''}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function DisparidadesAlegraDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(EMAIL_DEFAULT);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/condiciones-pago-crud/disparidades-alegra')
      .then((r) => setData(r.data))
      .catch(() => toast.error('No se pudo calcular el informe'))
      .finally(() => setLoading(false));
  }, [open]);

  const enviar = async () => {
    setEnviando(true);
    try {
      const r = await api.post('/condiciones-pago-crud/disparidades-alegra/enviar', { email });
      toast.success(`Informe enviado a ${r.data.enviado_a}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar el informe');
    } finally {
      setEnviando(false);
    }
  };

  const r = data?.resumen;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="dialog-disparidades-alegra">
        <DialogHeader>
          <DialogTitle>Disparidades Alegra ↔ ERP (Condiciones de Pago)</DialogTitle>
        </DialogHeader>
        {loading && <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>}
        {data && (
          <div className="space-y-2 text-sm">
            <div className="flex flex-wrap gap-2" data-testid="disparidades-resumen">
              <Badge variant="outline">Comparados: {r.proveedores_comparados}</Badge>
              <Badge className="bg-emerald-600">Iguales: {r.iguales}</Badge>
              <Badge className="bg-red-600">Distintos: {r.diferentes}</Badge>
              <Badge className="bg-amber-500">Sin equivalente: {r.sin_equivalente}</Badge>
              <Badge variant="outline">Maestro fuera de catálogo: {r.maestro_fuera_catalogo}</Badge>
              <Badge variant="outline">OCs desde 01-08 fuera de catálogo: {r.ocs_fuera_catalogo}</Badge>
            </div>
            {data.plazos_alegra_faltantes_en_erp.length > 0 && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded p-2 text-xs" data-testid="plazos-faltantes-alert">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <b>Plazos de Alegra que no existen en el ERP</b> (créalos con “Nueva Condición”):{' '}
                  {data.plazos_alegra_faltantes_en_erp.map((p) => `${p.alegra_term} (${p.dias} días)`).join(', ')}
                </div>
              </div>
            )}
            <Tabla
              titulo="Proveedores con condición distinta"
              headers={['RUT', 'Proveedor', 'Alegra', 'Equivalente ERP', 'Maestro ERP']}
              rows={data.diferentes.map((d) => [d.rut, d.nombre, d.alegra_term, d.sugerida_erp, d.erp_condicion || '(vacía)'])}
              vacio="Sin diferencias." testId="tabla-diferentes"
            />
            <Tabla
              titulo="Plazo Alegra sin equivalente en ERP"
              headers={['RUT', 'Proveedor', 'Alegra', 'Maestro ERP']}
              rows={data.sin_equivalente.map((d) => [d.rut, d.nombre, d.alegra_term, d.erp_condicion || '(vacía)'])}
              vacio="Todos los plazos tienen equivalente." testId="tabla-sin-equivalente"
            />
            <Tabla
              titulo="Maestro ERP con condición fuera del catálogo"
              headers={['RUT', 'Proveedor', 'Condición', 'Código']}
              rows={data.maestro_fuera_catalogo.map((d) => [d.rut, d.nombre, d.erp_condicion, d.erp_codigo])}
              vacio="Sin casos." testId="tabla-maestro-fuera"
            />
            <Tabla
              titulo="OCs desde 01-08-2026 con condición fuera del catálogo"
              headers={['OC', 'Fecha', 'Proveedor', 'Condición', 'Código', 'Estado']}
              rows={data.ocs_fuera_catalogo.map((o) => [o.numero_oc, o.fecha_norm, o.nombre_proveedor, o.condicion_pago, o.condicion_pago_codigo, o.estado])}
              vacio="Sin casos." testId="tabla-ocs-fuera"
            />
          </div>
        )}
        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex items-center gap-2 flex-1">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="correo destino" className="max-w-xs" data-testid="input-email-informe" />
            <Button onClick={enviar} disabled={enviando || !data || !email} className="gap-2" data-testid="btn-enviar-informe">
              {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Enviar informe
            </Button>
          </div>
          <Button variant="outline" onClick={onClose} data-testid="btn-cerrar-disparidades">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
